import { type PropsWithChildren, type RefObject } from 'preact/compat';
import { useState, useRef, useCallback, useEffect } from 'preact/hooks';
import './Popover.less';
import { autoUpdate, computePosition, type Placement } from '@floating-ui/dom';
import OutsideFocusHandler from '../utils/OutsideFocusHandler';

export type PopoverProps = PropsWithChildren<{
    anchorRef: RefObject<HTMLElement>;
    isOpen: boolean;
    onClose: () => void;
    placement?: Placement;
}>;

const Popover = ({
    anchorRef,
    isOpen,
    onClose,
    placement = 'bottom-end',
    children
}: PopoverProps) => {
    const popoverRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const onFocusOutside = useCallback(
        (event: MouseEvent | TouchEvent | FocusEvent) => {
            if (!anchorRef.current?.contains(event.target as Node)) {
                onClose();
            }
        },
        []
    );

    const updatePosition = useCallback(async () => {
        if (!isOpen || !anchorRef.current || !popoverRef.current) {
            return;
        }
        const { x, y } = await computePosition(
            anchorRef.current,
            popoverRef.current,
            { placement }
        );
        setPosition({ top: y, left: x });
    }, [anchorRef.current, popoverRef.current, placement, isOpen]);

    useEffect(() => {
        if (isOpen && anchorRef.current && popoverRef.current) {
            return autoUpdate(
                anchorRef.current,
                popoverRef.current,
                updatePosition
            );
        }
    }, [anchorRef.current, popoverRef.current, updatePosition, isOpen]);

    return (
        <div
            className={`bri-Popover ${isOpen ? 'bri-visible' : ''} ${
                placement.includes('top')
                    ? 'bri-down-entrance'
                    : 'bri-up-entrance'
            }`}
            ref={popoverRef}
            style={position}
        >
            <OutsideFocusHandler
                onClickOutside={onFocusOutside}
                onFocusOutside={onFocusOutside}
            >
                {children}
            </OutsideFocusHandler>
        </div>
    );
};

export default Popover;
